/**
 * entities/session/model/sessionStore — Zustand session store.
 *
 * Holds the in-memory access token and the authenticated user profile.
 * The access token is NEVER written to localStorage or sessionStorage.
 * The refresh token is an httpOnly cookie managed exclusively by the backend.
 *
 * Public API (consumed by dev-2-route-guards and auth feature slices):
 *   State:
 *     accessToken      — in-memory JWT string or null
 *     user             — CurrentUser | null
 *     status           — SessionStatus
 *     isAuthenticated  — boolean derived from status
 *     isBootstrapping  — boolean derived from status
 *
 *   Actions:
 *     setToken(token)  — store a new access token in memory
 *     clearToken()     — remove the token (does NOT call logout endpoint)
 *     setUser(user)    — store the user profile
 *     clearSession()   — clear token + user + set status = unauthenticated
 *     login(token, user) — atomic: set token + user + status = authenticated
 *     logout()         — call POST /api/auth/logout/, then clearSession()
 *     bootstrap()      — on app load: try GET /me; on 401 try POST /refresh
 *                        then retry /me; on failure call clearSession()
 *     refresh()        — POST /api/auth/refresh/ → store new token; throws on failure
 */

import { create } from 'zustand';
import { apiClient } from '@/shared/api';
import { setTokenAccessor } from '@/shared/auth/token';
import { setUnauthorizedHandler } from '@/shared/api';
import type { CurrentUser, SessionStatus } from './types';

// ---------------------------------------------------------------------------
// Store shape
// ---------------------------------------------------------------------------

export interface SessionState {
  // --- State ---
  accessToken: string | null;
  user: CurrentUser | null;
  status: SessionStatus;
  isAuthenticated: boolean;
  isBootstrapping: boolean;

  // --- Actions ---
  setToken: (token: string) => void;
  clearToken: () => void;
  setUser: (user: CurrentUser) => void;
  clearSession: () => void;
  login: (token: string, user: CurrentUser) => void;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  refresh: () => Promise<string>;
}

// ---------------------------------------------------------------------------
// Store implementation
// ---------------------------------------------------------------------------

export const useSessionStore = create<SessionState>((set, get) => ({
  // --- Initial state ---
  accessToken: null,
  user: null,
  status: 'idle',
  isAuthenticated: false,
  isBootstrapping: false,

  // --- Actions ---

  setToken(token: string) {
    set({ accessToken: token });
  },

  clearToken() {
    set({ accessToken: null });
  },

  setUser(user: CurrentUser) {
    set({ user });
  },

  clearSession() {
    set({
      accessToken: null,
      user: null,
      status: 'unauthenticated',
      isAuthenticated: false,
      isBootstrapping: false,
    });
  },

  login(token: string, user: CurrentUser) {
    set({
      accessToken: token,
      user,
      status: 'authenticated',
      isAuthenticated: true,
      isBootstrapping: false,
    });
  },

  async logout() {
    // Fire the logout endpoint — don't block UI on error (cookie will expire anyway).
    try {
      await apiClient.POST('/api/auth/logout/');
    } catch {
      // Ignore — clearSession regardless.
    }
    get().clearSession();
  },

  async refresh(): Promise<string> {
    const { data, error } = await apiClient.POST('/api/auth/refresh/');
    if (error !== undefined || !data) {
      throw new Error('refresh_failed');
    }
    const token = data.access;
    set({ accessToken: token });
    return token;
  },

  async bootstrap() {
    const { status } = get();
    // Only bootstrap once.
    if (status !== 'idle') return;

    set({ status: 'bootstrapping', isBootstrapping: true });

    try {
      // Try to fetch the current user. The API client attaches the access token
      // (if any) via the token accessor. For a fresh page load the token is null,
      // so /me will return 401 — that triggers the 401 handler (refresh + retry).
      // We need to handle that cycle ourselves here to avoid recursion.
      const { data: meData, response: meResponse } = await apiClient.GET('/api/auth/me/');

      if (meData) {
        set({
          user: meData,
          status: 'authenticated',
          isAuthenticated: true,
          isBootstrapping: false,
        });
        return;
      }

      // If /me returns 401 without the middleware intercepting (e.g., during bootstrap
      // itself before the handler is wired), try refresh manually.
      if (meResponse.status === 401) {
        try {
          const newToken = await get().refresh();
          set({ accessToken: newToken });

          // Retry /me with the new token.
          const { data: retryData } = await apiClient.GET('/api/auth/me/');
          if (retryData) {
            set({
              user: retryData,
              status: 'authenticated',
              isAuthenticated: true,
              isBootstrapping: false,
            });
            return;
          }
        } catch {
          // Refresh failed — fall through to unauthenticated.
        }
      }

      get().clearSession();
    } catch {
      // Network error or unrecoverable — treat as unauthenticated.
      get().clearSession();
    }
  },
}));

// ---------------------------------------------------------------------------
// Wire the session store into the shared/auth seams.
//
// Called once at module evaluation time so the API client uses the in-memory
// token from this store for every request after the module is imported.
// ---------------------------------------------------------------------------

/** Accessor that reads the live in-memory token from the Zustand store. */
function getTokenFromStore(): string | null {
  return useSessionStore.getState().accessToken;
}

setTokenAccessor(getTokenFromStore);

/**
 * 401 handler: attempt a silent token refresh once, then retry.
 * On refresh failure, clear the session.
 *
 * Note: the retry logic for the original request lives in the middleware
 * (shared/api/middleware.ts). This handler is called by the middleware on
 * every 401. We update the in-memory token; the middleware uses the token
 * accessor on the next request, so the refreshed token is automatically used.
 */
setUnauthorizedHandler(async () => {
  try {
    await useSessionStore.getState().refresh();
  } catch {
    useSessionStore.getState().clearSession();
  }
});
